import { useMemo } from 'react';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useAccountId } from '@atlassian/jira-tenant-context-controller';
import type { NudgeState, NudgeStateKey } from './types';
import { getUserProperty, setUserProperty } from './user-properties';

const KEY_V1 = 'CROSS_JOIN_NUDGES_CONFLUENCE';
const KEY_V2A = 'CROSS_JOIN_NUDGES_CONFLUENCE_V2A';

const getNudgeState = async (key: NudgeStateKey, accountId: string | null): Promise<NudgeState> => {
	if (!accountId) return 'NONE';
	return getState(accountId, key);
};

const setNudgeDismissState = async (key: NudgeStateKey, accountId: string | null) => {
	if (!accountId) return;
	await setState(accountId, key, 'DISMISSED');
};

const getState = async (accountId: string, nudgeStateKey: NudgeStateKey): Promise<NudgeState> =>
	(
		await getUserProperty<NudgeState>({
			accountId,
			propertyKey: nudgeStateKey,
			defaultValue: 'NONE',
		})
	).value;

const setState = async (
	accountId: string,
	nudgeStateKey: NudgeStateKey,
	nudgeStage: NudgeState,
) => {
	try {
		await setUserProperty<NudgeState>({
			accountId,
			propertyKey: nudgeStateKey,
			propertyValue: nudgeStage,
		});
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'setCrossJoinNudgeConfluenceState',
				packageName: 'jiraCrossJoinNudges',
			},
			error,
		});
	}
};

const useNudgeState = (key: NudgeStateKey) => {
	const accountId = useAccountId();
	return useMemo(
		() => ({
			getNudgeState: () => getNudgeState(key, accountId),
			setNudgeDismissed: () => setNudgeDismissState(key, accountId),
		}),
		[accountId, key],
	);
};

export const useNudgeStateV1 = () => useNudgeState(KEY_V1);
export const useNudgeStateV2a = () => useNudgeState(KEY_V2A);
